import CryptoJS from 'crypto-js';

export const parseJwtPayload = (token: string) => {
  const parts = token.split('.');
  if (parts.length !== 3) throw new Error('Invalid JWT format');
  const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '==='.slice((b64.length + 3) % 4);
  const words = CryptoJS.enc.Base64.parse(padded);
  const json = CryptoJS.enc.Utf8.stringify(words);
  return JSON.parse(json);
};

export const convertTimestampToDate = (timestamp: number) => {
  const date = new Date(timestamp * 1000);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}:${day}:${year}`;
};

export const convertPrice = (
  price: string | number,
  exChangeRate: number = 1
) => {
  return (
    Math.floor(Number(price) * exChangeRate * 100) / 100
  ).toLocaleString();
};

export const resizeImageFromUrl = (url: string, maxSize = 200) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;

      if (width > height) {
        if (width > maxSize) {
          height *= maxSize / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width *= maxSize / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.7));
    };
    img.src = url;
  });
};

export const getDisplayedOriginal = (price: any, originalPrice: any) => {
  if (!originalPrice) return '';
  const orig = Number(originalPrice);
  return orig === Number(price) ? orig * 1.3 : orig;
};

/**
 * Extract distinct properties from SKU list
 * Each property can have any name (color, size, material, etc.)
 */
export interface PropertyValue {
  name: string; // Property name (e.g., 'color', 'size', 'material')
  values: string[]; // List of distinct values for this property
  imageMap?: Map<string, string>; // For properties like color, map value to image URL
}

export interface ExtractedProperties {
  properties: PropertyValue[];
  propertyNames: string[]; // List of all property names in order
}

/**
 * Parse propertiesName array and extract distinct properties
 * Example propertiesName: ['Color:Red', 'Size:M', 'Material:Cotton']
 */
export const extractProperties = (skuList: any[]): ExtractedProperties => {
  const propertyMap = new Map<string, Set<string>>();
  const propertyImageMap = new Map<string, Map<string, string>>();
  const propertyOrder: string[] = [];

  for (const sku of skuList) {
    for (const prop of sku.propertiesName || []) {
      const [propName, propValue] = prop
        .split(':')
        .map((s: string) => s.trim());

      if (!propName || !propValue) continue;

      const lowerPropName = propName.toLowerCase();

      // Track property order (first occurrence)
      if (!propertyMap.has(lowerPropName)) {
        propertyOrder.push(lowerPropName);
        propertyMap.set(lowerPropName, new Set<string>());
        propertyImageMap.set(lowerPropName, new Map<string, string>());
      }

      // Add value to this property
      propertyMap.get(lowerPropName)!.add(propValue);

      // Store image URL for this property value (useful for color/image properties)
      if (
        sku.imageUrl &&
        !propertyImageMap.get(lowerPropName)!.has(propValue)
      ) {
        propertyImageMap.get(lowerPropName)!.set(propValue, sku.imageUrl);
      }
    }
  }

  // Convert to PropertyValue array
  const properties: PropertyValue[] = propertyOrder.map((propName) => ({
    name: propName,
    values: Array.from(propertyMap.get(propName)!),
    imageMap: propertyImageMap.get(propName),
  }));

  return {
    properties,
    propertyNames: propertyOrder,
  };
};

/**
 * Check if all required properties are selected
 * selectedValues: Object like { color: 'red', size: 'M' }
 * propertyNames: List of all property names in the SKU
 */
export const areAllPropertiesSelected = (
  selectedValues: Record<string, string>,
  propertyNames: string[]
): boolean => {
  return propertyNames.every((propName) => {
    const lowerPropName = propName.toLowerCase();
    return (
      selectedValues[lowerPropName] &&
      selectedValues[lowerPropName].trim() !== ''
    );
  });
};

/**
 * Find matching SKU based on selected properties
 * selectedValues: Object like { color: 'red', size: 'M' }
 */
export const findMatchingSKU = (
  skuList: any[],
  selectedValues: Record<string, string>
): any => {
  return skuList.find((sku) => {
    return sku.propertiesName.every((prop: string) => {
      const [propName, propValue] = prop
        .split(':')
        .map((s: string) => s.trim());
      const lowerPropName = propName.toLowerCase();
      return selectedValues[lowerPropName] === propValue;
    });
  });
};

export const formatCurrency = (value: number, currency: 'USD' | 'CNY') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatDateTime = (epochSeconds: number) => {
  const d = new Date(epochSeconds * 1000);
  return d.toLocaleString('en-US', { hour12: true });
};

export const statusBadge = (status: any['status']) => {
  switch (status) {
    case 'PENDING_PAYMENT':
      return {
        label: 'Pending Payment',
        className: 'bg-amber-100 text-amber-700 ring-1 ring-amber-200',
      };
    case 'PAID':
      return {
        label: 'Paid',
        className: 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200',
      };
    case 'PROCESSING':
      return {
        label: 'Processing',
        className: 'bg-sky-100 text-sky-700 ring-1 ring-sky-200',
      };
    case 'SHIPPED':
      return {
        label: 'Shipped',
        className: 'bg-indigo-100 text-indigo-700 ring-1 ring-indigo-200',
      };
    case 'DELIVERED':
      return {
        label: 'Delivered',
        className: 'bg-green-100 text-green-700 ring-1 ring-green-200',
      };
    case 'CANCELED':
      return {
        label: 'Canceled',
        className: 'bg-rose-100 text-rose-700 ring-1 ring-rose-200',
      };
    default:
      return {
        label: status,
        className: 'bg-gray-100 text-gray-700 ring-1 ring-gray-200',
      };
  }
};

export const paymentStatusBadge = (status: any['status']) => {
  switch (status) {
    case 'PENDING':
      return {
        label: 'Pending Payment',
        className: 'bg-amber-50 text-amber-700 ring-1 ring-amber-200',
      };
    case 'SUCCEEDED':
      return {
        label: 'Payment Successful',
        className: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200',
      };
    case 'FAILED':
      return {
        label: 'Payment Failed',
        className: 'bg-rose-50 text-rose-700 ring-1 ring-rose-200',
      };
    case 'CANCELED':
      return {
        label: 'Payment Canceled',
        className: 'bg-gray-50 text-gray-700 ring-1 ring-gray-200',
      };
    default:
      return {
        label: status,
        className: 'bg-gray-50 text-gray-700 ring-1 ring-gray-200',
      };
  }
};
